import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  getCurrentPosition,
  isLocationPermissionGranted,
  mapGeolocationErrorToStatus,
} from '../utils/location';

const UserLocationContext = createContext(null);
const STORAGE_KEY = 'mashhad_user_location';
const LEGACY_LS_KEY = 'user_location';
const MIGRATION_FLAG_KEY = 'mashhad_location_prefs_migrated';
const LOCATION_CHECKED_KEY = 'mashhad_location_last_checked';

const isNative = () => Capacitor.isNativePlatform();

// ─── Storage helpers (async; Preferences on native, localStorage on web) ─────

function parseStoredLocation(raw) {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const lat = typeof parsed?.lat === 'number' ? parsed.lat : parseFloat(parsed?.lat);
    const lng = typeof parsed?.lng === 'number' ? parsed.lng : parseFloat(parsed?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      lat,
      lng,
      city: parsed.city || null,
      district: parsed.district || null,
    };
  } catch {
    return null;
  }
}

function loadFromLocalStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const fromPrimary = parseStoredLocation(raw);
    if (fromPrimary) return fromPrimary;

    const legacy = localStorage.getItem(LEGACY_LS_KEY);
    const fromLegacy = parseStoredLocation(legacy);
    if (fromLegacy) {
      const migrated = { ...fromLegacy, city: fromLegacy.city || null, district: fromLegacy.district || null };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated)); } catch {}
      return migrated;
    }
    return null;
  } catch {
    return null;
  }
}

async function isMigrationDone() {
  if (isNative()) {
    try {
      const { value } = await Preferences.get({ key: MIGRATION_FLAG_KEY });
      return value === 'true';
    } catch {
      return false;
    }
  }
  try {
    return localStorage.getItem(MIGRATION_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
}

async function markMigrationDone() {
  if (isNative()) {
    try {
      await Preferences.set({ key: MIGRATION_FLAG_KEY, value: 'true' });
    } catch {}
    return;
  }
  try { localStorage.setItem(MIGRATION_FLAG_KEY, 'true'); } catch {}
}

async function migrateLocalStorageToPreferences() {
  const loc = loadFromLocalStorage();
  if (!loc) {
    await markMigrationDone();
    return null;
  }
  try {
    await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(loc) });
    await markMigrationDone();
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_LS_KEY);
    } catch {}
    return loc;
  } catch {
    return loc;
  }
}

async function loadStoredLocation() {
  if (isNative()) {
    try {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      const fromPrefs = parseStoredLocation(value);
      if (fromPrefs) return fromPrefs;

      if (!(await isMigrationDone())) {
        return migrateLocalStorageToPreferences();
      }
      return null;
    } catch {
      return loadFromLocalStorage();
    }
  }
  return loadFromLocalStorage();
}

async function saveLocation(loc) {
  const payload = loc ? JSON.stringify(loc) : null;
  if (isNative()) {
    try {
      if (payload) await Preferences.set({ key: STORAGE_KEY, value: payload });
      else await Preferences.remove({ key: STORAGE_KEY });
      return;
    } catch {
      try {
        if (payload) localStorage.setItem(STORAGE_KEY, payload);
        else localStorage.removeItem(STORAGE_KEY);
      } catch {}
      return;
    }
  }
  try {
    if (payload) localStorage.setItem(STORAGE_KEY, payload);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

async function getLastLocationCheckTime() {
  if (isNative()) {
    try {
      const { value } = await Preferences.get({ key: LOCATION_CHECKED_KEY });
      return parseInt(value || '0', 10) || 0;
    } catch {
      return 0;
    }
  }
  try {
    return parseInt(localStorage.getItem(LOCATION_CHECKED_KEY) || '0', 10) || 0;
  } catch {
    return 0;
  }
}

async function setLastLocationCheckTime(now) {
  const str = String(now);
  if (isNative()) {
    try {
      await Preferences.set({ key: LOCATION_CHECKED_KEY, value: str });
      return;
    } catch {}
  }
  try { localStorage.setItem(LOCATION_CHECKED_KEY, str); } catch {}
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useUserLocation = () => {
  const ctx = useContext(UserLocationContext);
  if (!ctx) {
    return {
      userLocation: null,
      locationStatus: 'idle',
      requestLocation: () => {},
      setUserLocationManually: () => {},
    };
  }
  return ctx;
};

// ─── Reverse geocoding (Nominatim — free, no API key) ───────────────────────

async function reverseGeocode(lat, lng) {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=ar&zoom=14`,
      { signal: controller.signal, headers: { 'Accept-Language': 'ar' } }
    );
    clearTimeout(timer);
    if (!res.ok) return null;
    const data = await res.json();
    const addr = data?.address || {};

    const cleanProvince = addr.province
      ? addr.province.replace(/^محافظة\s+/, '').replace(/^امارة\s+/i, '').trim()
      : null;

    const city =
      cleanProvince ||
      addr.city ||
      addr.town ||
      addr.village ||
      addr.city_district ||
      addr.municipality ||
      null;

    const district =
      addr.suburb || addr.neighbourhood || addr.quarter || null;

    return city ? { city, district: district || null } : null;
  } catch {
    return null;
  }
}

// ─── Distance helper ─────────────────────────────────────────────────────────

const CITY_CHANGE_THRESHOLD_KM = 50;
const LOCATION_MAX_AGE_MS = 60 * 60 * 1000;

function calcDistanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ─── Position helpers ────────────────────────────────────────────────────────

function isSecureCtx() {
  if (typeof window === 'undefined') return false;
  if (Capacitor.isNativePlatform()) return true;
  const h = window.location.hostname;
  return (
    window.isSecureContext ||
    window.location.protocol === 'https:' ||
    h === 'localhost' ||
    h === '127.0.0.1' ||
    /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(h)
  );
}

// ─── Provider ────────────────────────────────────────────────────────────────

export const UserLocationProvider = ({ children }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle');
  const [storageReady, setStorageReady] = useState(false);
  const userLocationRef = useRef(null);
  const gpsBootstrappedRef = useRef(false);

  useEffect(() => {
    userLocationRef.current = userLocation;
  }, [userLocation]);

  // Effect A — hydrate from storage on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = await loadStoredLocation();
        if (cancelled) return;
        if (stored) {
          setUserLocation(stored);
          setLocationStatus('granted');
        }
      } finally {
        if (!cancelled) setStorageReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Effect B — persist when storage is ready
  useEffect(() => {
    if (!storageReady) return;
    saveLocation(userLocation).catch(() => {});
  }, [userLocation, storageReady]);

  const setUserLocationManually = useCallback((location) => {
    if (!location) { setUserLocation(null); setLocationStatus('idle'); return; }
    const { lat, lng, city = null, district = null } = location;
    const latNum = typeof lat === 'number' ? lat : parseFloat(lat);
    const lngNum = typeof lng === 'number' ? lng : parseFloat(lng);
    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return;
    setUserLocation({ lat: latNum, lng: lngNum, city, district });
    setLocationStatus('granted');
  }, []);

  const requestLocation = useCallback(async () => {
    if (!isSecureCtx()) { setLocationStatus('insecure'); return; }
    setLocationStatus('loading');
    try {
      const position = await getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 30000,
      });

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      setUserLocation({ lat, lng });
      setLocationStatus('granted');

      reverseGeocode(lat, lng).then((geo) => {
        if (geo) setUserLocation((prev) => ({ ...prev, city: geo.city, district: geo.district }));
      });
    } catch (err) {
      setLocationStatus(mapGeolocationErrorToStatus(err));
    }
  }, []);

  // Effect C — reverse geocode when coords exist without city
  useEffect(() => {
    if (!storageReady) return;
    if (userLocation?.lat && userLocation?.lng && !userLocation?.city) {
      reverseGeocode(userLocation.lat, userLocation.lng).then((geo) => {
        if (geo) setUserLocation((prev) => ({ ...prev, city: geo.city, district: geo.district }));
      });
    }
  }, [userLocation?.lat, userLocation?.lng, storageReady]);

  // Effect D — GPS bootstrap after storage hydration
  useEffect(() => {
    if (!storageReady) return;
    if (gpsBootstrappedRef.current) return;
    gpsBootstrappedRef.current = true;

    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem('onboarding_completed') !== 'true') return;

    const coords = userLocationRef.current;
    if (!coords) {
      requestLocation();
      return;
    }

    (async () => {
      const lastChecked = await getLastLocationCheckTime();
      if (Date.now() - lastChecked < LOCATION_MAX_AGE_MS) return;

      const permissionGranted = await isLocationPermissionGranted();
      if (!permissionGranted) return;

      try {
        const pos = await getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 30000,
        });
        const newLat = pos.coords.latitude;
        const newLng = pos.coords.longitude;

        await setLastLocationCheckTime(Date.now());

        const current = userLocationRef.current;
        if (current?.lat && current?.lng) {
          const dist = calcDistanceKm(current.lat, current.lng, newLat, newLng);
          if (dist < CITY_CHANGE_THRESHOLD_KM) return;
        }

        setUserLocation((prev) => ({ ...prev, lat: newLat, lng: newLng }));

        reverseGeocode(newLat, newLng).then((geo) => {
          if (geo) setUserLocation((prev) => ({ ...prev, city: geo.city, district: geo.district }));
        });
      } catch {
        await setLastLocationCheckTime(Date.now());
      }
    })();
  }, [storageReady, requestLocation]);

  return (
    <UserLocationContext.Provider
      value={{ userLocation, locationStatus, requestLocation, setUserLocationManually }}
    >
      {children}
    </UserLocationContext.Provider>
  );
};
